import { useContext, type ReactNode } from "react";

import { DisclosurePanel } from "#components/v2/Disclosure";
import { ArrowsInIcon, ArrowsOutIcon } from "#icons";
import { cn } from "#utils";
import {
  Button as AriaButton,
  Disclosure as AriaDisclosure,
  DisclosureStateContext as AriaDisclosureStateContext,
} from "react-aria-components";

import { EntityCardHeader, type EntityCardHeaderProps } from "./EntityCardHeader";
import { cardBase } from "./styles";

/** Props for the ExpandableEntityCard component */
export interface ExpandableEntityCardProps extends Omit<EntityCardHeaderProps, "trailing"> {
  /** Content revealed beneath the header row when the card is expanded. */
  expandedContent?: ReactNode;
  /** Accessible label for the expand/collapse toggle. Defaults to "Toggle details". */
  expandToggleLabel?: string;
  /** Additional class names merged onto the card */
  className?: string;
}

/** Trailing expand/collapse toggle that reflects the surrounding disclosure state. */
function ExpandToggle({ label }: { label: string }) {
  const { isExpanded } = useContext(AriaDisclosureStateContext) ?? {
    isExpanded: false,
  };
  return (
    <AriaButton
      aria-label={label}
      className="size-4 flex shrink-0 cursor-pointer items-center justify-center rounded-sm text-text-primary outline-none focus-visible:ring-2 focus-visible:ring-outline-brand"
      slot="trigger"
    >
      {isExpanded ? <ArrowsInIcon size={16} /> : <ArrowsOutIcon size={16} />}
    </AriaButton>
  );
}

/**
 * Disclosure-style EntityCard: the shared `EntityCardHeader` plus a trailing
 * expand/collapse toggle, with `expandedContent` revealed beneath the header in
 * an animated panel. Shares the card surface and header arrangement with
 * `EntityCard`; use that component when no expansion is needed.
 */
export function ExpandableEntityCard({
  icon,
  label,
  title,
  meta,
  aside,
  layout = "inline",
  expandedContent,
  expandToggleLabel = "Toggle details",
  className,
}: ExpandableEntityCardProps) {
  return (
    <AriaDisclosure className={cn(cardBase, "flex-col p-sm", className)}>
      <div className={cn("flex w-full gap-xs", layout === "stacked" ? "flex-col" : "items-center")}>
        <EntityCardHeader
          aside={aside}
          icon={icon}
          label={label}
          layout={layout}
          meta={meta}
          title={title}
          trailing={<ExpandToggle label={expandToggleLabel} />}
        />
      </div>
      <DisclosurePanel>
        <div className="pt-xs">{expandedContent}</div>
      </DisclosurePanel>
    </AriaDisclosure>
  );
}
